import { Lock, Sparkle } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { usePlanFlags, UPGRADE_REASON } from "@/contexts/PlanFlagsContext";

/**
 * Wraps a primary create button so it locks + shows an upgrade tooltip when
 * the corresponding plan flag is `false`. `flagKey` maps to
 * PlanFlagsContext's flag object (e.g. `can_add_branch`, `has_multi_branch`).
 */
export function PlanGatedButton({ flagKey, children, disabled, className = "", ...btnProps }) {
  const { flags } = usePlanFlags();
  const allowed = flags?.[flagKey] !== false;   // default permissive
  if (allowed) {
    return <Button disabled={disabled} className={className} {...btnProps}>{children}</Button>;
  }
  return (
    <Button
      disabled
      className={`opacity-60 cursor-not-allowed gap-1.5 ${className}`}
      title={UPGRADE_REASON[flagKey] || "Not included in your plan"}
      {...btnProps}
    >
      <Lock size={14} weight="duotone" /> {children}
    </Button>
  );
}

/**
 * Full-width banner that surfaces when a feature is unavailable — used at
 * the top of the tab (e.g. Branding, Excel import) so the user understands
 * why fields are locked.
 */
export function UpgradeBanner({ flagKey, hidden }) {
  const { flags } = usePlanFlags();
  const allowed = flags?.[flagKey] !== false;
  if (allowed || hidden) return null;
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 flex items-center gap-3 text-sm text-amber-900" data-testid={`upgrade-banner-${flagKey}`}>
      <Sparkle size={18} weight="duotone" className="text-amber-700 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-medium">Available on higher plans</div>
        <div className="text-xs text-amber-900/80 mt-0.5">{UPGRADE_REASON[flagKey] || "This feature is not included in your current plan."}</div>
      </div>
      <Link to="/billing/subscription" className="shrink-0">
        <Button size="sm" variant="outline" className="border-amber-300 text-amber-900 hover:bg-amber-100">Manage plan</Button>
      </Link>
    </div>
  );
}
